import { LineLayer } from '@deck.gl/layers';
import { BufferTransform, Model, Geometry } from '@luma.gl/engine';
import { Texture } from '@luma.gl/core';
import { isViewportGlobe, getViewportGlobeCenter, getViewportGlobeRadius, getViewportBounds } from './viewport.js';
import { particleLayerUniforms, ParticleLayerUniforms } from './particle-layer-uniforms';
import updateTransformVs from './particle-layer-update-transform.vs.glsl?raw';
import vertexShader from './particle-vertex-shader.glsl?raw';
import { LayerContext } from '@deck.gl/core';

let FPS: number;

const DEFAULT_COLOR = [255, 255, 255, 255];

const defaultProps = {
    ...LineLayer.defaultProps,

    //image: { type: 'image', value: null, async: true },
    imageUnscale: { type: 'array', value: null },

    numParticles: { type: 'number', min: 1, max: 1000000, value: 5000 },
    maxAge: { type: 'number', min: 1, max: 255, value: 100 },
    speedFactor: { type: 'number', min: 0, max: 1, value: 1 },

    color: { type: 'color', value: DEFAULT_COLOR },
    width: { type: 'number', value: 1 },
    animate: true,

    bounds: { type: 'array', value: [-180, -90, 180, 90], compare: true },
    wrapLongitude: true,
    fps: { type: 'number', value: 18 }
};

export default class ParticleLayer extends LineLayer {
    getShaders() {
        return {
            ...super.getShaders(),
            vs: vertexShader,
            inject: {
                'vs:#decl': `
          out float drop;
          const vec2 DROP_POSITION = vec2(0);
        `,
                'vs:#main-start': `
          drop = float(instanceSourcePositions.xy == DROP_POSITION || instanceTargetPositions.xy == DROP_POSITION);
        `,
                'fs:#decl': `
          in float drop;
        `,
                'fs:#main-start': `
          if (drop > 0.5) discard;
        `
            }
        };
    }

    initializeState() {
        FPS = (this.props as any).fps;
        super.initializeState();

        this._setupTransformFeedback();

        const attributeManager = this.getAttributeManager();
        attributeManager!.remove(['instanceSourcePositions', 'instanceTargetPositions', 'instanceColors', 'instanceWidths']);
    }

    updateState({ props, oldProps, changeFlags }: { props: any; oldProps: any; changeFlags: any }) {
        const { numParticles, maxAge, color, width } = props;

        super.updateState({ props, oldProps, changeFlags } as any);

        if (!numParticles || !maxAge || !width) {
            this._deleteTransformFeedback();
            return;
        }

        if (
            numParticles !== oldProps.numParticles ||
            maxAge !== oldProps.maxAge ||
            color[0] !== oldProps.color[0] ||
            color[1] !== oldProps.color[1] ||
            color[2] !== oldProps.color[2] ||
            color[3] !== oldProps.color[3] ||
            width !== oldProps.width
        ) {
            this._setupTransformFeedback();
        }
    }

    finalizeState(context: LayerContext) {
        (this.state as any)?.texture?.destroy();
        this._deleteTransformFeedback();
        super.finalizeState(context);
    }

    draw() {
        const { initialized } = this.state as any;
        if (!initialized) {
            return;
        }

        const { animate } = this.props as any;
        const { sourcePositions, targetPositions, colors, model } = this.state as any;

        model.setAttributes({
            instanceSourcePositions: sourcePositions,
            instanceTargetPositions: targetPositions,
            instanceColors: colors
        });

        super.draw({} as any);

        if (animate) {
            this.requestStep();
        }
    }

    _getModel() {
        /*
         *  (0, -1)-------------_(1, -1)
         *       |          _,-"  |
         *       o      _,-"      o
         *       |  _,-"          |
         *   (0, 1)"-------------(1, 1)
         */
        const positions = [0, -1, 0, 0, 1, 0, 1, -1, 0, 1, 1, 0];

        return new Model(this.context.device, {
            ...this.getShaders(),
            id: this.props.id,
            //bufferLayout: this.getAttributeManager()?.getBufferLayouts(),
            bufferLayout: [
                { name: 'instanceSourcePositions', format: 'float32x3' },
                { name: 'instanceTargetPositions', format: 'float32x3' },
                { name: 'instanceColors', format: 'unorm8x4' },
                { name: 'instanceWidths', format: 'float32' }
            ],
            geometry: new Geometry({
                topology: 'triangle-strip',
                attributes: {
                    positions: { size: 3, value: new Float32Array(positions) }
                }
            }),
            isInstanced: true,
            instanceCount: 1,
            constantAttributes: {
                instanceWidths: new Float32Array([(this.props as any).width ?? 2])
            }
        });
    }

    _setupTransformFeedback() {
        const { device } = this.context;

        const { initialized, texture } = this.state as any;
        if (initialized) {
            this._deleteTransformFeedback();
        }

        const { numParticles, maxAge, color, image } = this.props as any;

        // sourcePositions/targetPositions buffer layout:
        // |          age0         |          age1         |          age2         |...|          ageN         |
        // |pos1,pos2,pos3,...,posN|pos1,pos2,pos3,...,posN|pos1,pos2,pos3,...,posN|...|pos1,pos2,pos3,...,posN|
        const numInstances = numParticles * maxAge;
        const numAgedInstances = numParticles * (maxAge - 1);
        const sourcePositions = device.createBuffer(new Float32Array(numInstances * 3));
        const targetPositions = device.createBuffer(new Float32Array(numInstances * 3));
        const sourcePositions64Low = new Float32Array([0, 0, 0]); // constant attribute
        const targetPositions64Low = new Float32Array([0, 0, 0]); // constant attribute

        const colors = device.createBuffer(
            new Uint8Array(
                new Array(numInstances)
                    .fill(undefined)
                    .map((_, i) => {
                        const age = Math.floor(i / numParticles);
                        return [color[0], color[1], color[2], (color[3] ?? 255) * (1 - age / maxAge)];
                    })
                    .flat()
            )
        );
        let tex: Texture = texture;
        if (!tex) {
            tex = device.createTexture({
                ...image,
                format: 'rgba8unorm',
                sampler: {
                    minFilter: 'linear',
                    mipmapFilter: 'linear',
                    magFilter: 'linear',
                    addressModeU: 'clamp-to-edge',
                    addressModeV: 'clamp-to-edge'
                },
                mipLevels: device.getMipLevelCount(image.width, image.height)
            });
        }

        const transform = new BufferTransform(device, {
            attributes: {
                sourcePosition: sourcePositions
            },
            feedbackBuffers: {
                targetPosition: targetPositions
            },
            varyings: ['targetPosition'],
            bufferLayout: [{ name: 'sourcePosition', format: 'float32x3' }],
            vs: updateTransformVs,
            modules: [particleLayerUniforms],
            vertexCount: numInstances
        });

        this.setState({
            initialized: true,
            numInstances,
            numAgedInstances,
            sourcePositions,
            targetPositions,
            sourcePositions64Low,
            targetPositions64Low,
            colors,
            texture: tex,
            transform
        });
    }

    _runTransformFeedback() {
        const { initialized } = this.state as any;
        if (!initialized) {
            return;
        }

        const { viewport, timeline } = this.context;
        const { imageUnscale, bounds, numParticles, speedFactor, maxAge } = this.props as any;
        const { numAgedInstances, transform, previousViewportZoom, previousTime, sourcePositions, targetPositions, texture } = this
            .state as any;

        const time = timeline.getTime();
        if (!texture || time === previousTime) {
            return;
        }

        // viewport
        const viewportGlobe = isViewportGlobe(viewport);
        const viewportGlobeCenter = getViewportGlobeCenter(viewport);
        const viewportGlobeRadius = getViewportGlobeRadius(viewport);
        const viewportBounds = getViewportBounds(viewport);
        const viewportZoomChangeFactor = 2 ** ((previousViewportZoom - viewport.zoom) * 4);

        // speed factor for current zoom level
        const currentSpeedFactor = speedFactor / 2 ** (viewport.zoom + 7);

        // update particles age0
        const uniforms: ParticleLayerUniforms & { bitmapTexture: Texture } = {
            bitmapTexture: texture,
            viewportGlobe,
            viewportGlobeCenter: (viewportGlobeCenter || [0, 0]) as [number, number],
            viewportGlobeRadius: viewportGlobeRadius || 0,
            viewportBounds: (viewportBounds || [0, 0, 0, 0]) as [number, number, number, number],
            viewportZoomChangeFactor: viewportZoomChangeFactor || 0,
            imageUnscale: imageUnscale || [0, 0],
            bounds,
            numParticles,
            maxAge,
            speedFactor: currentSpeedFactor,
            time,
            seed: Math.random()
        };
        transform.model.shaderInputs.setProps({ feParticle: uniforms });
        // transform.model.setBindings({
        //   bitmapTexture: image,
        // });

        //sourcePositions.readAsync().then((x) => console.log('Before', new Float32Array(x)));
        transform.run();

        // update particles age1-age(N-1)
        // copy age0-age(N-2) sourcePositions to age1-age(N-1) targetPositions
        this._copyBuffer(targetPositions, sourcePositions, 0, numParticles * 4 * 3, numAgedInstances * 4 * 3);

        // Swap
        transform.model.setAttributes({ sourcePosition: sourcePositions });
        transform.transformFeedback.setBuffers({ targetPosition: targetPositions });

        //sourcePositions.readAsync().then((x) => console.log('After swap', new Float32Array(x)));

        this.setState({
            sourcePositions: targetPositions,
            targetPositions: sourcePositions,
            transform
        });

        (this.state as any).previousViewportZoom = viewport.zoom;
        (this.state as any).previousTime = time;
    }

    _copyBuffer(readBuffer: any, writeBuffer: any, readOffset: number, writeOffset: number, size: number) {
        const { gl } = this.context.device as any;
        gl.bindBuffer(gl.COPY_READ_BUFFER, readBuffer.handle);
        gl.bindBuffer(gl.COPY_WRITE_BUFFER, writeBuffer.handle);
        gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, readOffset, writeOffset, size);
        gl.bindBuffer(gl.COPY_READ_BUFFER, null);
        gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
    }

    _resetTransformFeedback() {
        const { initialized } = this.state as any;
        if (!initialized) {
            return;
        }

        const { numInstances, sourcePositions, targetPositions } = this.state as any;

        sourcePositions.write(new Float32Array(numInstances * 3));
        targetPositions.write(new Float32Array(numInstances * 3));
    }

    _deleteTransformFeedback() {
        const { initialized } = this.state as any;
        if (!initialized) {
            return;
        }

        const { sourcePositions, targetPositions, colors, transform } = this.state as any;

        sourcePositions.delete();
        targetPositions.delete();
        colors.delete();
        transform.destroy();
        //texture?.destroy(); // This is destroyed in finalizeState(). The texture needs to be destroyed at the very end of the layer livecycle.

        this.setState({
            initialized: false,
            sourcePositions: undefined,
            targetPositions: undefined,
            sourcePositions64Low: undefined,
            targetPositions64Low: undefined,
            colors: undefined,
            widths: undefined,
            transform: undefined
        });
    }

    requestStep() {
        const { stepRequested } = this.state as any;
        if (stepRequested) {
            return;
        }

        (this.state as any).stepRequested = true;
        self.setTimeout(() => {
            this.step();
            (this.state as any).stepRequested = false;
        }, 1000 / FPS);
    }

    step() {
        this._runTransformFeedback();
        this.setNeedsRedraw();
    }

    clear() {
        this._resetTransformFeedback();
        this.setNeedsRedraw();
    }
}

ParticleLayer.layerName = 'ParticleLayer';
ParticleLayer.defaultProps = defaultProps;
